import { InteractiveMapBlockElements } from '@features/Components/InteractiveMapBlock/Scripts/interactiveMapBlock.types';

type UseInteractiveMapStateParameters = {
    blockClass: string;
    blockElement: HTMLElement;
    interactiveElements: InteractiveMapBlockElements;
    interactionScopeElement: HTMLElement;
};

const useInteractiveMapState = ({
    blockClass,
    blockElement,
    interactiveElements,
    interactionScopeElement,
}: UseInteractiveMapStateParameters) => {
    const activeInfoPointClass = `${blockClass}__info-point--active`;
    const highlightedInfoPointClass = `${blockClass}__info-point--highlighted`;
    const activeInfoListItemClass = `${blockClass}__info-list-item--active`;
    const highlightedInfoListItemClass = `${blockClass}__info-list-item--highlighted`;
    const activeMapClass = `${blockClass}__map--has-active-info-point`;

    const getInfoPointElement = (number: string): HTMLDivElement | null => {
        return blockElement.querySelector(`.${blockClass}__info-point[data-number='${number}']`);
    };

    const getInfoPointDetailsElement = (number: string): HTMLElement | null => {
        return getInfoPointElement(number)?.querySelector(`.${blockClass}__info-point-details`) ?? null;
    };

    const getInfoListItemElement = (number: string): HTMLLIElement | null => {
        return blockElement.querySelector(`.${blockClass}__info-list-item[data-number='${number}']`);
    };

    const getInfoListButtonElement = (number: string): HTMLButtonElement | null => {
        return getInfoListItemElement(number)?.querySelector(`.${blockClass}__info-list-item-button`) ?? null;
    };

    const getActiveNumber = (): string | null => {
        return blockElement.querySelector<HTMLLIElement>(`.${activeInfoListItemClass}`)?.dataset.number ?? null;
    };

    const clearHighlightedNumbers = (): void => {
        interactionScopeElement.querySelectorAll<HTMLDivElement>(`.${blockClass}__info-point`).forEach((infoPointElement) => {
            infoPointElement.classList.remove(highlightedInfoPointClass);
        });

        interactionScopeElement.querySelectorAll<HTMLLIElement>(`.${blockClass}__info-list-item`).forEach((infoListItemElement) => {
            infoListItemElement.classList.remove(highlightedInfoListItemClass);
        });
    };

    const clearActiveNumbers = (): void => {
        interactionScopeElement.querySelectorAll<HTMLDivElement>(`.${blockClass}__map`).forEach((mapElement) => {
            mapElement.classList.remove(activeMapClass);
        });

        interactionScopeElement.querySelectorAll<HTMLDivElement>(`.${blockClass}__info-point`).forEach((infoPointElement) => {
            const infoPointDetailsElement = infoPointElement.querySelector(`.${blockClass}__info-point-details`);

            infoPointElement.classList.remove(activeInfoPointClass);
            infoPointDetailsElement?.setAttribute('aria-hidden', 'true');
        });

        interactionScopeElement.querySelectorAll<HTMLLIElement>(`.${blockClass}__info-list-item`).forEach((infoListItemElement) => {
            const infoListButtonElement = infoListItemElement.querySelector(`.${blockClass}__info-list-item-button`);

            infoListItemElement.classList.remove(activeInfoListItemClass);
            infoListButtonElement?.setAttribute('aria-expanded', 'false');
        });
    };

    const highlightNumber = (number: string): void => {
        const infoPointElement = getInfoPointElement(number);
        const infoListItemElement = getInfoListItemElement(number);

        if (!infoPointElement || !infoListItemElement) {
            return;
        }

        clearHighlightedNumbers();

        infoPointElement.classList.add(highlightedInfoPointClass);
        infoListItemElement.classList.add(highlightedInfoListItemClass);
    };

    const activateNumber = (number: string): void => {
        const infoPointElement = getInfoPointElement(number);
        const infoPointDetailsElement = getInfoPointDetailsElement(number);
        const infoListItemElement = getInfoListItemElement(number);
        const infoListButtonElement = getInfoListButtonElement(number);

        if (!infoPointElement || !infoPointDetailsElement || !infoListItemElement || !infoListButtonElement) {
            return;
        }

        clearHighlightedNumbers();
        clearActiveNumbers();

        interactiveElements.mapElement.classList.add(activeMapClass);
        infoPointElement.classList.add(activeInfoPointClass);
        infoPointDetailsElement.setAttribute('aria-hidden', 'false');
        infoListItemElement.classList.add(activeInfoListItemClass);
        infoListButtonElement.setAttribute('aria-expanded', 'true');
    };

    return {
        activateNumber,
        clearActiveNumbers,
        clearHighlightedNumbers,
        getActiveNumber,
        highlightNumber,
    };
};

export default useInteractiveMapState;
